import { hudResources, sessionTimeLimitMs } from './schema.js';
import { easeOutCubic, lerp, prefersReducedMotion } from './motion.js';
import type { AssetLoader } from './AssetLoader.js';
import type { GameConfig, Resource, UiConfig } from '../types.js';

export const DEFAULT_THEME = 'wood';

const DEFAULT_SPRITES: Record<string, string> = {
    logo: 'logo.png',
    frame: 'cloud-frame.png',
    resourcePill: 'res-pill.png',
    planner: 'planner.png',
    shop: 'btn-shop.svg',
    hammer: 'btn-hammer.png',
    book: 'btn-book.png',
    home: 'btn-home.svg',
    options: 'btn-options.svg',
    optionsMuted: 'btn-options-muted.svg',
};

const BUTTONS: { id: string; corner: 'bottomLeft' | 'bottomRight' }[] = [
    { id: 'shop', corner: 'bottomLeft' },
    { id: 'hammer', corner: 'bottomRight' },
    { id: 'book', corner: 'bottomRight' },
    { id: 'options', corner: 'bottomRight' },
];

const COUNT_UP_MS = 420;

const COUNT_UP_LIMIT = 500;

export const DESIGN_WIDTH = 1512;
export const DESIGN_HEIGHT = 914;

interface HudOptions {
    config: GameConfig;
    assetLoader: AssetLoader;
    root: HTMLElement;
}

type HudAction = string;

export class Hud {
    private readonly config: GameConfig;
    private readonly assetLoader: AssetLoader;
    private readonly root: HTMLElement;
    private readonly ui: UiConfig;
    private readonly handlers = new Map<HudAction, (action: HudAction) => void>();
    private readonly valueNodes = new Map<string, HTMLElement>();
    private readonly shownValues = new Map<string, number>();
    private readonly counters = new Map<string, number>();
    private readonly chromeBase: string;
    private scale = 1;

    private plannerSubtitle: HTMLElement | null = null;
    private levelValue: HTMLElement | null = null;
    private levelEra: HTMLElement | null = null;
    private sessionTimer: HTMLElement | null = null;
    private optionsButton: HTMLButtonElement | null = null;
    private optionsIcon: HTMLImageElement | null = null;

    constructor({ config, assetLoader, root }: HudOptions) {
        this.config = config;
        this.assetLoader = assetLoader;
        this.root = root;
        this.ui = config.ui ?? {};
        this.chromeBase = `img/${this.ui.interface || DEFAULT_THEME}/`;
    }

    /**
     * Every URL that could satisfy this sprite — theme art first, then the
     * wood set — is preloaded; spriteUrl() picks whichever loaded.
     */
    private spriteCandidates(key: string): string[] {
        const configured = (this.ui as Record<string, unknown>)[key];
        if (typeof configured === 'string') return [this.assetLoader.resolve(configured)];
        const fallback = DEFAULT_SPRITES[key];
        if (!fallback) return [];

        const urls = [this.chromeBase + fallback];
        if (this.chromeBase !== `img/${DEFAULT_THEME}/`) {
            urls.push(`img/${DEFAULT_THEME}/${fallback}`);
        }
        return urls;
    }

    private spriteUrl(key: string): string | null {
        const candidates = this.spriteCandidates(key);
        for (const url of candidates) {
            if (this.assetLoader.getImageUrl(url)) return url;
        }
        return candidates[0] ?? null;
    }

    private plannerIconUrl(): string {
        const icon = this.ui.planner?.icon;
        if (icon) return this.assetLoader.resolve(icon);
        const candidates = this.spriteCandidates('planner');
        for (const url of candidates) {
            if (this.assetLoader.getImageUrl(url)) return url;
        }
        return candidates[0] ?? this.chromeBase + DEFAULT_SPRITES.planner!;
    }

    private resourceIconCandidates(resource: Resource): string[] {
        if (resource.icon) return [this.assetLoader.resolve(resource.icon)];
        const file = `res-${resource.id}.png`;
        const urls = [`${this.chromeBase}${file}`];
        if (this.chromeBase !== `img/${DEFAULT_THEME}/`) {
            urls.push(`img/${DEFAULT_THEME}/${file}`);
        }
        return urls;
    }

    private resourceIconUrl(resource: Resource): string {
        const candidates = this.resourceIconCandidates(resource);
        for (const url of candidates) {
            if (this.assetLoader.getImageUrl(url)) return url;
        }
        return candidates[0] ?? '';
    }

    spriteUrls(): string[] {
        const urls: string[] = [];
        Object.keys(DEFAULT_SPRITES)
            .filter((key) => key !== 'planner')
            .forEach((key) => urls.push(...this.spriteCandidates(key)));
        urls.push(...this.spriteCandidates('planner'));
        hudResources(this.config).forEach((r) => urls.push(...this.resourceIconCandidates(r)));
        return [...new Set(urls)].filter(Boolean);
    }

    private loaded(url: string | null): HTMLImageElement | null {
        return url ? this.assetLoader.getImageUrl(url) : null;
    }

    build(): void {
        this.root.innerHTML = '';

        this.buildFrame();
        const topLeft = this.group('topLeft');
        const topRight = this.group('topRight');
        const bottomLeft = this.group('bottomLeft');
        const bottomRight = this.group('bottomRight');
        const bottomCenter = this.group('bottomCenter');

        const homeCorner = this.ui.homeCorner;

        this.buildLogo(topLeft);
        this.buildResources(topRight, homeCorner);
        this.buildSessionTimer(topRight);
        this.buildPlanner(bottomLeft);
        this.buildLevelBar(bottomCenter);

        const corners = { bottomLeft, bottomRight };
        if (homeCorner) this.buildButton(corners[homeCorner], 'home');

        const buttons = BUTTONS.filter(({ id }) => id !== 'shop' || Boolean(this.config.shop));
        buttons.forEach(({ id, corner }) => this.buildButton(corners[corner], id));

        this.buildDebug();
        this.applyScale();
    }

    private group(corner: string): HTMLElement {
        const el = document.createElement('div');
        // Containers must not intercept map gestures.
        el.className = `hud-group hud-group--${toKebab(corner)}`;
        this.root.appendChild(el);
        return el;
    }

    private buildFrame(): void {
        const url = this.spriteUrl('frame');
        if (!this.loaded(url)) return;

        const frame = document.createElement('div');
        frame.className = 'hud-frame';
        frame.style.backgroundImage = `url("${cssUrl(url!)}")`;
        this.root.appendChild(frame);
    }

    private buildLogo(parent: HTMLElement): void {
        const url = this.spriteUrl('logo');
        const el = document.createElement('div');
        el.className = 'hud-logo';

        if (this.loaded(url)) {
            const img = document.createElement('img');
            img.src = url!;
            img.alt = this.config.gameTitle ?? '';
            el.appendChild(img);
        } else {
            el.classList.add('hud-logo--text');
            el.textContent = this.config.gameTitle ?? '';
        }

        parent.appendChild(el);
    }

    private buildResources(parent: HTMLElement, homeCorner?: 'bottomLeft' | 'bottomRight' | null): void {
        const pillUrl = this.spriteUrl('resourcePill');
        const hasPill = Boolean(this.loaded(pillUrl));

        const row = document.createElement('div');
        row.className = 'hud-resources';

        hudResources(this.config).forEach((resource) => {
            const pill = document.createElement('div');
            pill.className = hasPill ? 'hud-pill hud-pill--art' : 'hud-pill';
            if (hasPill) pill.style.backgroundImage = `url("${cssUrl(pillUrl!)}")`;

            const iconUrl = this.resourceIconUrl(resource);
            if (this.loaded(iconUrl)) {
                const icon = document.createElement('img');
                icon.className = 'hud-pill__icon';
                icon.src = iconUrl;
                icon.alt = resource.label || resource.id;
                pill.appendChild(icon);
            } else {
                const label = document.createElement('span');
                label.className = 'hud-pill__label';
                label.textContent = resource.label || resource.id;
                pill.appendChild(label);
            }

            const value = document.createElement('span');
            value.className = 'hud-pill__value';
            value.textContent = formatValue(resource.initial ?? 0);
            pill.appendChild(value);

            this.valueNodes.set(resource.id, value);
            this.shownValues.set(resource.id, resource.initial ?? 0);
            row.appendChild(pill);
        });

        if (!homeCorner) this.buildButton(row, 'home', 'hud-button--inline');

        parent.appendChild(row);
    }

    private buildSessionTimer(parent: HTMLElement): void {
        if (sessionTimeLimitMs(this.config) == null) return;

        this.sessionTimer = document.createElement('div');
        this.sessionTimer.className = 'hud-session';
        this.sessionTimer.setAttribute('aria-live', 'polite');
        this.sessionTimer.textContent = 'TIME --:--';
        parent.appendChild(this.sessionTimer);
    }

    setSessionTime(remainingMs: number): void {
        if (!this.sessionTimer) return;
        this.sessionTimer.textContent = `TIME ${formatDuration(remainingMs)}`;
    }

    setEnabled(enabled: boolean): void {
        this.root.classList.toggle('is-disabled', !enabled);
        this.root.querySelectorAll('button').forEach((button) => {
            button.disabled = !enabled;
        });
    }

    private buildPlanner(parent: HTMLElement): void {
        const planner = this.ui.planner;
        if (!planner) return;

        const url = this.plannerIconUrl();
        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'hud-planner';
        el.addEventListener('click', () => this.emit('planner'));

        if (this.loaded(url)) {
            const img = document.createElement('img');
            img.className = 'hud-planner__icon';
            img.src = url;
            img.alt = '';
            el.appendChild(img);
        }

        const text = document.createElement('div');
        text.className = 'hud-planner__text';

        const title = document.createElement('span');
        title.className = 'hud-planner__title';
        title.textContent = planner.title || 'Planner';
        text.appendChild(title);

        this.plannerSubtitle = document.createElement('span');
        this.plannerSubtitle.className = 'hud-planner__subtitle';
        this.plannerSubtitle.textContent = planner.subtitle ?? '';
        text.appendChild(this.plannerSubtitle);

        el.appendChild(text);
        parent.appendChild(el);
    }

    setPlannerCount(count: number): void {
        if (!this.plannerSubtitle) return;
        this.plannerSubtitle.textContent = `${count} ${count === 1 ? 'task' : 'tasks'} for growth`;
    }

    private buildLevelBar(parent: HTMLElement): void {
        const level = this.ui.level;
        if (!level) return;

        const bar = document.createElement('div');
        bar.className = 'hud-level';

        const addIcon = (src: string | null | undefined, position: 'start' | 'end') => {
            if (!src) return;
            const url = this.assetLoader.resolve(src);
            if (!this.loaded(url)) return;
            const img = document.createElement('img');
            img.className = `hud-level__icon hud-level__icon--${position}`;
            img.src = url;
            img.alt = '';
            bar.appendChild(img);
        };

        addIcon(level.iconStart, 'start');

        const text = document.createElement('div');
        text.className = 'hud-level__text';

        this.levelValue = document.createElement('span');
        this.levelValue.className = 'hud-level__value';
        this.levelValue.textContent = `LEVEL ${level.value ?? 1}`;
        text.appendChild(this.levelValue);

        if (level.era) {
            this.levelEra = document.createElement('span');
            this.levelEra.className = 'hud-level__era';
            this.levelEra.textContent = level.era;
            text.appendChild(this.levelEra);
        }

        bar.appendChild(text);
        addIcon(level.iconEnd, 'end');

        parent.appendChild(bar);
    }

    setLevel(value: number, era?: string): void {
        if (this.levelValue) this.levelValue.textContent = `LEVEL ${value}`;
        if (this.levelEra && era != null) this.levelEra.textContent = era;
    }

    private buildButton(parent: HTMLElement, id: string, extraClass = ''): void {
        const url = this.spriteUrl(id);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = extraClass ? `hud-button ${extraClass}` : 'hud-button';
        button.dataset.action = id;
        button.setAttribute('aria-label', id);

        if (this.loaded(url)) {
            const img = document.createElement('img');
            img.src = url!;
            img.alt = '';
            button.appendChild(img);
            if (id === 'options') this.optionsIcon = img;
        } else {
            button.classList.add('hud-button--text');
            button.textContent = id;
        }

        if (id === 'options') this.optionsButton = button;
        button.addEventListener('click', () => this.emit(id));
        parent.appendChild(button);
    }

    /** Swaps the OPTIONS button icon between enabled and muted states. */
    setOptionsMuted(muted: boolean): void {
        const button = this.optionsButton;
        if (!button) return;

        if (this.optionsIcon) {
            const url = this.spriteUrl(muted ? 'optionsMuted' : 'options');
            if (url) this.optionsIcon.src = url;
        } else {
            button.classList.toggle('is-muted', muted);
        }
        button.setAttribute('aria-label', muted ? 'options (muted)' : 'options');
    }

    private buildDebug(): void {
        const debug = document.createElement('div');
        debug.id = 'hudDebug';
        debug.textContent = '–';
        this.root.appendChild(debug);
    }

    setResource(id: string, value: number): void {
        const node = this.valueNodes.get(id);
        if (!node) return;

        const from = this.shownValues.get(id);
        this.shownValues.set(id, value);

        const jump = from == null ? Infinity : Math.abs(value - from);
        if (jump === 0) return;

        if (jump > COUNT_UP_LIMIT || prefersReducedMotion()) {
            this.cancelCount(id);
            node.textContent = formatValue(value);
            return;
        }

        this.countUp(id, node, from!, value);
    }

    private countUp(id: string, node: HTMLElement, from: number, to: number): void {
        this.cancelCount(id);

        node.textContent = formatValue(to);

        const started = performance.now();
        const step = (now: number): void => {
            const t = easeOutCubic((now - started) / COUNT_UP_MS);
            node.textContent = formatValue(Math.round(lerp(from, to, t)));

            if (t < 1) {
                this.counters.set(id, requestAnimationFrame(step));
            } else {
                this.counters.delete(id);
            }
        };

        this.counters.set(id, requestAnimationFrame(step));
    }

    private cancelCount(id: string): void {
        const raf = this.counters.get(id);
        if (raf != null) cancelAnimationFrame(raf);
        this.counters.delete(id);
    }

    on(action: HudAction, handler: (action: HudAction) => void): void {
        this.handlers.set(action, handler);
    }

    private emit(action: HudAction): void {
        this.handlers.get(action)?.(action);
    }

    setViewport(width: number, height: number): void {
        if (!width || !height) return;
        const fit = Math.min(width / DESIGN_WIDTH, height / DESIGN_HEIGHT);
        this.scale = Math.min(Math.max(fit, 0.45), 1.5);
        this.applyScale();
    }

    destroy(): void {
        this.counters.forEach((raf) => cancelAnimationFrame(raf));
        this.counters.clear();
        this.handlers.clear();
    }

    private applyScale(): void {
        this.root.style.setProperty('--ui-scale', String(this.scale));
    }
}

function formatValue(value: number | string | undefined): string {
    return typeof value === 'number' ? String(value) : String(value ?? '');
}

function formatDuration(ms: number): string {
    const total = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function cssUrl(url: string): string {
    return url.replace(/["\\]/g, '\\$&');
}

function toKebab(value: string): string {
    return value.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}
